import React, {
  createContext,
  useContext,
  useRef,
  useState,
  useEffect,
  RefObject,
} from "react";

/* Components */
import ResizerButton from "src/components/shared/buttons/resizer-button/ResizerButton";

interface ResizeContextProps {
  height: number;
  width: number;
  contentRef: RefObject<HTMLElement>;
  containerRef: RefObject<HTMLElement>;
  resizerRef: RefObject<HTMLDivElement>;
}

interface ResizeProviderProps {
  children: React.ReactNode;
  withResizer?: boolean;
}

export const ResizeContext =
  createContext<ResizeContextProps | null>(null);

export const useResize = () => {
  const context = useContext(ResizeContext);
  if (!context) throw new Error("ResizeContext not found");
  return context;
};

export const ResizeProvider: React.FC<
  ResizeProviderProps
> = ({ children, withResizer = false }) => {
  const containerRef = useRef<HTMLElement>(null);
  const resizerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLElement>(null);

  const [height, setHeight] = useState(452);
  const [width, setWidth] = useState(0);

  // Watch width
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const observer = new ResizeObserver(([entry]) => {
      if (entry?.contentRect.width > 0) {
        setWidth(entry.contentRect.width);
      }
    });

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Handle drag
  useEffect(() => {
    const resizerEl = resizerRef.current;
    if (!resizerEl) return;

    let startY = 0;
    let startHeight = 0;

    const onMouseDown = (e: MouseEvent) => {
      e.preventDefault();
      startY = e.clientY;
      startHeight = height;

      const onMouseMove = (e: MouseEvent) => {
        const deltaY = e.clientY - startY;
        setHeight(Math.max(200, startHeight + deltaY));
      };

      const onMouseUp = () => {
        document.removeEventListener(
          "mousemove",
          onMouseMove
        );
        document.removeEventListener("mouseup", onMouseUp);
      };

      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
    };

    resizerEl.addEventListener("mousedown", onMouseDown);
    return () =>
      resizerEl.removeEventListener(
        "mousedown",
        onMouseDown
      );
  }, [resizerRef, height]);

  return (
    <ResizeContext.Provider
      value={{
        height,
        width,
        containerRef,
        contentRef,
        resizerRef,
      }}
    >
      {children}
      {withResizer ? (
        <ResizerButton ref={resizerRef} />
      ) : undefined}
    </ResizeContext.Provider>
  );
};
